import React, { useMemo, useState, useEffect } from 'react';
import { getMe, getPendingUsers, getUsers } from '../api/users';
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import Icon from "@mui/material/Icon";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import Skeleton from "@mui/material/Skeleton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import UserDetailsModal from "../components/UserDetailsModal";

/**
 * Helper to get initials from a name string.
 * @param {string} name
 * @returns {string}
 */
function getInitials(name) {
  if (!name) return "";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Profile component for displaying user avatar, name, and email.
 * @param {Object} props
 * @param {string} props.image
 * @param {string} props.name
 * @param {string} props.email
 */
const Profile = ({ image, name, email, onClick }) => (
  <MDBox display="flex" alignItems="center" lineHeight={1}>
    {image ? (
      <MDAvatar src={image} name={name} size="sm" />
    ) : (
      <MDAvatar bgColor="info" size="sm">{getInitials(name)}</MDAvatar>
    )}
    <MDBox ml={2} lineHeight={1}>
      <MDTypography
        display="block"
        variant="button"
        fontWeight="medium"
        sx={onClick ? { cursor: "pointer", "&:hover": { color: "info.main", textDecoration: "underline" } } : {}}
        onClick={onClick}
      >
        {name}
      </MDTypography>
      <MDTypography variant="caption">{email}</MDTypography>
    </MDBox>
  </MDBox>
);

/**
 * Skeleton loader for the users table.
 */
function UsersTableSkeleton() {
  return (
    <MDBox>
      {[...Array(6)].map((_, i) => (
        <MDBox key={i} display="flex" alignItems="center" py={2} borderBottom="1px solid #eee">
          {/* user */}
          <MDBox display="flex" alignItems="center" width="45%">
            <Skeleton variant="circular" width={36} height={36} />
            <MDBox ml={2}>
              <Skeleton variant="text" width={80} height={20} />
              <Skeleton variant="text" width={120} height={16} />
            </MDBox>
          </MDBox>
          {/* role */}
          <MDBox width="20%" ml={2}>
            <Skeleton variant="text" width={60} height={18} />
          </MDBox>
          {/* branch */}
          <MDBox width="15%" display="flex" justifyContent="center">
            <Skeleton variant="text" width={60} height={18} />
          </MDBox>
          {/* actions */}
          <MDBox width="20%" display="flex" justifyContent="center" gap={1}>
            <Skeleton variant="rectangular" width={60} height={32} />
          </MDBox>
        </MDBox>
      ))}
    </MDBox>
  );
}

/**
 * Users page for managing user accounts. Fetches users from the backend and displays them in a DataTable.
 * Includes search, loading skeleton, and error handling.
 */
export const Users = () => {
  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [jobTitleFilter, setJobTitleFilter] = useState("");
  const [jobTitleMenuAnchorEl, setJobTitleMenuAnchorEl] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingPending, setLoadingPending] = useState(false);
  const [errorUsers, setErrorUsers] = useState(null);
  const [errorPending, setErrorPending] = useState(null);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState(0); // 0 = all users, 1 = pending users
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedPendingUser, setSelectedPendingUser] = useState(null);

  const canSeePendingTab = me?.role_id === 1 || me?.role_id === 2;

  useEffect(() => {
    getMe()
      .then((data) => setMe(data))
      .catch(() => setMe(null));
  }, []);

  useEffect(() => {
    if (activeTab === 1 && !canSeePendingTab) setActiveTab(0);
  }, [activeTab, canSeePendingTab]);

  const fetchUsersList = React.useCallback(async () => {
    setLoadingUsers(true);
    setErrorUsers(null);
    try {
      const data = await getUsers();
      setUsers(data.users || []);
      return data.users || [];
    } catch (err) {
      setErrorUsers(err.message || "Failed to fetch users");
      setUsers([]);
      return [];
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const fetchPendingUsersList = React.useCallback(async () => {
    setLoadingPending(true);
    setErrorPending(null);
    try {
      const data = await getPendingUsers();
      const list = data.users || data.pending_users || [];
      setPendingUsers(list);
      return list;
    } catch (err) {
      setErrorPending(err.message || "Failed to fetch pending users");
      setPendingUsers([]);
      return [];
    } finally {
      setLoadingPending(false);
    }
  }, []);

  // Fetch All users on mount.
  useEffect(() => {
    fetchUsersList();
  }, [fetchUsersList]);

  // Fetch pending users once we know we can see them.
  useEffect(() => {
    if (!canSeePendingTab) return;
    fetchPendingUsersList();
  }, [canSeePendingTab, fetchPendingUsersList]);

  const jobTitleOptions = useMemo(() => {
    // Options "made up of" job titles present in the loaded users list
    const counts = new Map();
    users.forEach((u) => {
      const id = u?.job_title_id;
      if (id === null || id === undefined || id === "") return;
      const key = String(id);
      const name = u?.job_title_name || `Job title ${key}`;
      const existing = counts.get(key);
      counts.set(key, {
        id: key,
        name,
        count: (existing?.count || 0) + 1,
      });
    });

    return Array.from(counts.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [users]);

  const selectedJobTitleLabel = useMemo(() => {
    if (!jobTitleFilter) return "All job titles";
    const match = jobTitleOptions.find((jt) => String(jt.id) === String(jobTitleFilter));
    return match ? match.name : `Job title ${jobTitleFilter}`;
  }, [jobTitleFilter, jobTitleOptions]);

  const currentUsers = activeTab === 0 ? users : pendingUsers;
  const currentLoading = activeTab === 0 ? loadingUsers : loadingPending;
  const currentError = activeTab === 0 ? errorUsers : errorPending;

  const sortedUsers = useMemo(() => {
    const getFirstName = (u) => {
      const name = (u?.full_name || u?.name || "").trim();
      return (name.split(/\s+/)[0] || "").toLowerCase();
    };
    return [...currentUsers].sort((a, b) => getFirstName(a).localeCompare(getFirstName(b)));
  }, [currentUsers]);

  const filteredUsers = useMemo(() => {
    const q = search.toLowerCase();

    const jobTitleMatches = (user) => {
      if (activeTab !== 0) return true;
      if (!jobTitleFilter) return true;
      return String(user.job_title_id ?? "") === String(jobTitleFilter);
    };

    return sortedUsers.filter((user) => (
      jobTitleMatches(user) &&
      (
        (user.full_name && user.full_name.toLowerCase().includes(q)) ||
        (user.name && user.name.toLowerCase().includes(q)) ||
        (user.email && user.email.toLowerCase().includes(q))
      )
    ));
  }, [sortedUsers, search, activeTab, jobTitleFilter]);

  const columns = useMemo(() => {
    if (activeTab === 1) {
      return [
        { Header: "user", accessor: "user", width: "55%", align: "left" },
        { Header: "status", accessor: "status", align: "center" },
        { Header: "requested", accessor: "requested", align: "center" },
      ];
    }

    return [
      { Header: "user", accessor: "user", width: "45%", align: "left" },
      { Header: "job title", accessor: "jobTitle", align: "left" },
      { Header: "branch", accessor: "branch", align: "center" },
      { Header: "action", accessor: "action", align: "center" },
    ];
  }, [activeTab]);

  const rows = useMemo(() => {
    if (activeTab === 1) {
      return filteredUsers.map((user) => ({
        user: (
          <Profile
            image={user.profile_pic_path}
            name={user.full_name || user.name}
            email={user.email}
            onClick={() => {
              setSelectedPendingUser(user);
              setSelectedUserId(null);
              setIsUserModalOpen(true);
            }}
          />
        ),
        status: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {user.status || 'pending'}
          </MDTypography>
        ),
        requested: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
          </MDTypography>
        ),
      }));
    }

    return filteredUsers.map((user) => ({
      user: (
        <Profile
          image={user.profile_pic_path}
          name={user.full_name}
          email={user.email}
          onClick={() => {
            setSelectedUserId(user.id);
            setSelectedPendingUser(null);
            setIsUserModalOpen(true);
          }}
        />
      ),
      jobTitle: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {user.job_title_name || 'N/A'}
        </MDTypography>
      ),
      branch: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {user.branch_name || 'N/A'}
        </MDTypography>
      ),
      action: (
        <MDButton
          variant="text"
          color="dark"
          onClick={() => {
            setSelectedUserId(user.id);
            setSelectedPendingUser(null);
            setIsUserModalOpen(true);
          }}
        >
          <Icon>visibility</Icon>&nbsp;view
        </MDButton>
      ),
    }));
  }, [activeTab, filteredUsers]);

  // Render the main content (loading, error, or table)
  const renderContent = () => {
    if (currentLoading) {
      return (
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              Users
            </MDTypography>
          </MDBox>
          <MDBox px={2} pt={3} pb={1} display="flex" flexDirection="column" gap={1}>
            <MDBox px={0.5} mb={0.5} display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <MDBox
                sx={{
                  backgroundColor: "grey.100",
                  borderRadius: "12px",
                  padding: "4px",
                  display: "inline-flex",
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  TabIndicatorProps={{ style: { display: "none" } }}
                  sx={{
                    minHeight: "unset",
                    "& .MuiTab-root": {
                      minHeight: "unset",
                      textTransform: "none",
                      fontSize: 14,
                      fontWeight: 600,
                      padding: "8px 12px",
                      borderRadius: "10px",
                      color: "text.secondary",
                    },
                    "& .Mui-selected": {
                      backgroundColor: "#fff",
                      color: "text.primary",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                    },
                  }}
                >
                  <Tab label="All users" />
                  {canSeePendingTab && <Tab label="Pending users" />}
                </Tabs>
              </MDBox>
              <MDButton
                variant="text"
                color="info"
                size="small"
                onClick={() => {
                  fetchUsersList();
                  if (canSeePendingTab) fetchPendingUsersList();
                }}
                disabled={loadingUsers || loadingPending}
              >
                <Icon fontSize="small">refresh</Icon>&nbsp;Refresh
              </MDButton>
            </MDBox>
            <MDInput
              placeholder="Search users..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              fullWidth
              sx={{
                borderRadius: 2,
                px: 2,
                py: 1.5,
                fontSize: 15,
                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
              }}
            />
            <MDTypography variant="caption" color="text" mt={0.5} mb={0.5} pl={0.5}>
              {filteredUsers.length} user{filteredUsers.length === 1 ? '' : 's'} found
            </MDTypography>
          </MDBox>
          <MDBox pt={1}>
            <UsersTableSkeleton />
          </MDBox>
        </Card>
      );
    }

    if (currentError) {
      return (
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              Users
            </MDTypography>
          </MDBox>
          <MDBox px={2} pt={3} pb={2}>
            <MDBox px={0.5} mb={0.5} display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <MDBox
                sx={{
                  backgroundColor: "grey.100",
                  borderRadius: "12px",
                  padding: "4px",
                  display: "inline-flex",
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  TabIndicatorProps={{ style: { display: "none" } }}
                  sx={{
                    minHeight: "unset",
                    "& .MuiTab-root": {
                      minHeight: "unset",
                      textTransform: "none",
                      fontSize: 14,
                      fontWeight: 600,
                      padding: "8px 12px",
                      borderRadius: "10px",
                      color: "text.secondary",
                    },
                    "& .Mui-selected": {
                      backgroundColor: "#fff",
                      color: "text.primary",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                    },
                  }}
                >
                  <Tab label="All users" />
                  {canSeePendingTab && <Tab label="Pending users" />}
                </Tabs>
              </MDBox>
              <MDButton
                variant="text"
                color="info"
                size="small"
                onClick={() => {
                  fetchUsersList();
                  if (canSeePendingTab) fetchPendingUsersList();
                }}
                disabled={loadingUsers || loadingPending}
              >
                <Icon fontSize="small">refresh</Icon>&nbsp;Refresh
              </MDButton>
            </MDBox>
            <MDTypography color="error" variant="body2">
              Error loading {activeTab === 1 ? "pending users" : "users"}: {currentError}
            </MDTypography>
            <MDBox mt={2} display="flex" gap={1}>
              <MDButton
                variant="gradient"
                color="info"
                onClick={() => (activeTab === 1 ? fetchPendingUsersList() : fetchUsersList())}
                disabled={activeTab === 1 && !canSeePendingTab}
              >
                Retry
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>
      );
    }

    return (
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={3}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
        >
          <MDTypography variant="h6" color="white">
            Users
          </MDTypography>
        </MDBox>
        <MDBox px={2} pt={3} pb={1} display="flex" flexDirection="column" gap={1}>
          <MDBox px={0.5} mb={0.5} display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <MDBox
              sx={{
                backgroundColor: "grey.100",
                borderRadius: "12px",
                padding: "4px",
                display: "inline-flex",
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                TabIndicatorProps={{ style: { display: "none" } }}
                sx={{
                  minHeight: "unset",
                  "& .MuiTab-root": {
                    minHeight: "unset",
                    textTransform: "none",
                    fontSize: 14,
                    fontWeight: 600,
                    padding: "8px 12px",
                    borderRadius: "10px",
                    color: "text.secondary",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#fff",
                    color: "text.primary",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                  },
                }}
              >
                  <Tab label="All users" />
                  {canSeePendingTab && <Tab label="Pending users" />}
              </Tabs>
            </MDBox>
            <MDButton
              variant="text"
              color="info"
              size="small"
              onClick={() => {
                fetchUsersList();
                  if (canSeePendingTab) fetchPendingUsersList();
              }}
              disabled={loadingUsers || loadingPending}
            >
              <Icon fontSize="small">refresh</Icon>&nbsp;Refresh
            </MDButton>
          </MDBox>
          <MDBox display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
            <MDInput
              placeholder="Search users..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              fullWidth
              sx={{
                flex: 1,
                minWidth: 220,
                borderRadius: 2,
                px: 2,
                py: 1.5,
                fontSize: 15,
                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
              }}
            />
            {activeTab === 0 && (
              <>
                <MDButton
                  variant={jobTitleFilter ? "gradient" : "outlined"}
                  color="info"
                  onClick={(e) => setJobTitleMenuAnchorEl(e.currentTarget)}
                  sx={{
                    minWidth: 240,
                    justifyContent: "space-between",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontSize: 14,
                    fontWeight: 600,
                    height: "44px",
                    px: 1.5,
                    boxShadow: jobTitleFilter ? undefined : "0 1px 2px rgba(0,0,0,0.03)",
                    backgroundColor: jobTitleFilter ? undefined : "#fff",
                    borderColor: "grey.300",
                  }}
                >
                  <MDBox display="flex" alignItems="center" gap={1} sx={{ overflow: "hidden" }}>
                    <Icon fontSize="small">tune</Icon>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {selectedJobTitleLabel}
                    </MDTypography>
                  </MDBox>
                  <Icon fontSize="small">expand_more</Icon>
                </MDButton>

                <Menu
                  anchorEl={jobTitleMenuAnchorEl}
                  open={Boolean(jobTitleMenuAnchorEl)}
                  onClose={() => setJobTitleMenuAnchorEl(null)}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                >
                  <MenuItem
                    onClick={() => {
                      setJobTitleFilter("");
                      setJobTitleMenuAnchorEl(null);
                    }}
                    selected={!jobTitleFilter}
                  >
                    All job titles
                  </MenuItem>
                  {jobTitleOptions.map((jt) => (
                    <MenuItem
                      key={jt.id}
                      onClick={() => {
                        setJobTitleFilter(String(jt.id));
                        setJobTitleMenuAnchorEl(null);
                      }}
                      selected={String(jobTitleFilter) === String(jt.id)}
                    >
                      {jt.name} ({jt.count})
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </MDBox>
          <MDTypography variant="caption" color="text" mt={0.5} mb={0.5} pl={0.5}>
            {filteredUsers.length} {activeTab === 1 ? 'pending user' : 'user'}{filteredUsers.length === 1 ? '' : 's'} found
          </MDTypography>
        </MDBox>
        <MDBox pt={1}>
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            entriesPerPage={false}
            pagination={false}
            showTotalEntries={false}
            noEndBorder
          />
        </MDBox>
      </Card>
    );
  };

  // Main render
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        {renderContent()}
      </MDBox>
      <UserDetailsModal
        open={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        userId={selectedUserId}
        pendingUser={selectedPendingUser}
        onUpdated={(event) => {
          // From regular user edit: updated user row
          if (event?.id) {
            setUsers((prev) => prev.map((u) => (u.id === event.id ? { ...u, ...event } : u)));
            fetchUsersList();
            return;
          }

          // From pending flow: refresh both lists
          if (event?.type === "pending_approved" || event?.type === "pending_rejected") {
            fetchUsersList();
            fetchPendingUsersList();
          }

          if (event?.type === "user_deleted") {
            setUsers((prev) => prev.filter((u) => u.id !== event.id));
            fetchUsersList();
          }
        }}
      />
    </DashboardLayout>
  );
}; 